import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Alert, Box, Button, Container, Grid, Stack, Typography, Tooltip, Menu, List, ListItem } from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import lodash from 'lodash';

const API_BASE_URL = 'https://dev-ball-machine.waltair.io/api/v2'; // Replace with your actual API base URL

const actionLabels = {
  forehand: { title: 'Forehand', tooltipText: 'For Forehand shots' },
  backhand: { title: 'Backhand', tooltipText: 'For Backhand shots' },
  overhead_smash: { title: 'Serve / Overhead smash', tooltipText: 'Use this for both serves and overhead smash shots' },
  no_action: { title: 'No action', tooltipText: 'Player is just walking, letting the ball go' },
  not_sure: { title: 'Not sure', tooltipText: 'If you are completely confused on what this is' }
};

const PoseEstimationLabeling = () => {
  const [currentClip, setCurrentClip] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ message: '', severity: 'info' });
  // console.log('currentClip:', currentClip);
  const {
    sessionDetails,
  } = useSelector((state) => state.auth);
  // console.log('sessionDetails:', sessionDetails);

  const fetchClip = async () => {
    try {
      setIsLoading(true);
      setNotification({ message: 'Fetch in progress', severity: 'info' });
      const response = await fetch(`${API_BASE_URL}/pose-estimation-classification`);

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.message || 'Failed to fetch clip');
      }
      const data = await response.json();
      setIsLoading(false);
      setNotification({ message: 'Successfully fetched new clip!', severity: 'success' });
      return lodash.get(data, 'record', {});
    } catch (error) {
      console.error('Error fetching clip:', error);
      setIsLoading(false);
      setNotification({ message: error.message || 'Failed to fetch clip', severity: 'error' });
      return null;
    }
  };

  useEffect(() => {
    const fetchCurrentClip = async () => {
      const clip = await fetchClip();
      if (clip) {
        setCurrentClip(clip);
      }
    };
    fetchCurrentClip();
  }, []);

  const sendAction = async (action) => {
    try {
      setIsLoading(true);
      setNotification({ message: 'Update in progress', severity: 'info' });
      const response = await fetch(`${API_BASE_URL}/pose-estimation-classification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: currentClip.video_clip_file_name,
          classification: action,
          userId: `${sessionDetails.first_name}_${sessionDetails.last_name}`,
        }),
      });
      setIsLoading(false);
      if (!response.ok) {
        throw new Error('Failed to send action');
      }
      // console.log(`Action ${action} sent successfully.`);
      setNotification({
        message: `Successfully updated the clip classification to ${action.toUpperCase()}`,
        severity: 'success'
      });
    } catch (error) {
      setIsLoading(false);
      console.error('Error sending action:', error);
      setNotification({ message: 'Failed to update!', severity: 'error' });
    }
  };

  const handleNext = async () => {
    const nextClip = await fetchClip();
    if (nextClip) {
      setCurrentClip(nextClip);
    }
  };

  /* const handlePrevious = async () => {
    const prevClip = await fetchClip();
    if (prevClip) {
      setCurrentClip(prevClip);
    }
  }; */

  const getToolTipText = (buttonText) => {
    return actionLabels[buttonText]?.tooltipText || 'No tooltip available';
  };

  const getButtonTitle = (buttonText) => {
    return actionLabels[buttonText]?.title || buttonText.replace('_', ' ').toUpperCase();
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Container maxWidth="xl" style={{ marginTop: '2rem', textAlign: 'center' }}>
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Grid container spacing={2}>
          <Grid size={12} textAlign={'center'} style={{ width: '100%' }}>
            <Typography variant="h4" gutterBottom>
              {currentClip.video_clip_file_name}
            </Typography>
          </Grid>
          <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%">
            <Button
              id="help-menu-button"
              aria-controls={open ? 'help-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleClick}
            >
              Help
            </Button>
            <Menu
              id="help-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              slotProps={{
                list: {
                  'aria-labelledby': 'help-menu-button',
                },
              }}
            >
              <Box sx={{ width: '500px', minHeight: '200px', height: 'auto' }}>
                <List sx={{ width: '100%', maxWidth: 450, bgColor: 'background.paper' }}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <ChevronRightIcon/>
                    </ListItemIcon>
                    {'We are only interested in the action performed by the player on the other side of the net. ' +
                      'If there is no player on the other side of the net, mark it as "no action".'}
                  </ListItem>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <ChevronRightIcon/>
                    </ListItemIcon>
                    {'If the player is just walking, picking up the ball from ground' +
                      ' or letting the ball go, use "no action".'}
                  </ListItem>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <ChevronRightIcon/>
                    </ListItemIcon>
                    {'If the player serves the ball or performs an overhead smash, use "serve / overhead smash".'}
                  </ListItem>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <ChevronRightIcon/>
                    </ListItemIcon>
                    {'If you are absolutely not sure on what the action is, mark it as "not sure".'}
                  </ListItem>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <ChevronRightIcon/>
                    </ListItemIcon>
                    {'Even if certain action is not 100% complete, it should be marked as such.'}
                  </ListItem>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <ChevronRightIcon/>
                    </ListItemIcon>
                    {'If the clips involves doubles play, mark it as "not sure".'}
                  </ListItem>
                </List>
              </Box>
            </Menu>
          </Box>
          <Grid size={12} style={{ width: '100%', margin: '1rem 0' }}>
            {
              notification && notification.message ? (
                <Alert severity={notification.severity}>{notification.message}</Alert>
              ) : null
            }
          </Grid>
          <Grid size={12} textAlign={'center'} style={{ width: '100%', margin: '1rem 0', border: '0 solid blue' }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 1, sm: 2, md: 4 }}
            >
              <Box width={{ xs: '100%', md: '80%' }}>
                <video
                  controls
                  muted
                  autoPlay
                  playsInline
                  style={{ width: '100%', height: 'auto', marginBottom: '1rem' }}
                  src={currentClip.video_clip_file_url}
                />
              </Box>
              <Box width={{ xs: '100%', md: '20%', textAlign: 'center' }}>
                {
                  currentClip.video_clip_file_name && (
                    <>
                      <Typography variant="h6" gutterBottom>
                        Action Classification
                      </Typography>
                      <Stack spacing={2}>
                        {Object.keys(actionLabels).map((action) => (
                          <Tooltip title={getToolTipText(action)} key={action}>
                          <Button
                            key={action}
                            variant="outlined"
                            color="primary"
                            disabled={isLoading}
                            onClick={() => sendAction(action)}
                          >
                            {getButtonTitle(action)}
                          </Button>
                          </Tooltip>
                        ))}
                      </Stack>
                      <Stack spacing={2} sx={{ marginTop: '4rem' }}>
                        {/* <Grid size={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handlePrevious}
                      disabled={isLoading}
                    >
                      Previous
                    </Button>
                  </Grid> */}
                        <Grid item>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                            disabled={isLoading}
                          >
                            Next Clip
                          </Button>
                        </Grid>
                      </Stack>
                    </>
                  )
                }
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default PoseEstimationLabeling;
